import { useEffect } from 'react';
import { useAccountsRests } from '../api';
import { useAccountsStore } from './useAccountsStore';
import {
  getAccountBalanceWithDeltas,
  getAccountTypeCreatedDate,
  getBalanceBreakdown,
  getBaseAssetMeta,
  getGeneralBalance,
  getDeltaPercent,
  getGeneralMidnightBalance,
  getMarginAccountsFromMarginResponse,
  getMarginAccountsRests,
  getRegularAccountsFromRests,
  getRegularAccountsRests,
} from './calculations';
import type { AccontTypeData, MarginAccount, RegularAccount } from './types';
import { useAccountsByType } from './useAccountsByType';

export const useSyncAccountsStore = () => {
  const { accountsInfo, accountsByType } = useAccountsByType();
  const { data: accountsRests } = useAccountsRests();

  const {
    setEwallet,
    setSpot,
    setInvest,
    setLeverage,
    setEarn,
    setGeneral,
    setAccountsQty,
  } = useAccountsStore();

  useEffect(() => {
    if (!accountsRests || !accountsInfo || !accountsByType) return;

    const ewalletAccounts: AccontTypeData<RegularAccount>['accounts'] =
      getRegularAccountsFromRests(
        accountsRests?.ewallet || [],
        accountsInfo.accounts
      );
    const spotAccounts: AccontTypeData<RegularAccount>['accounts'] =
      getRegularAccountsFromRests(
        accountsRests?.spot || [],
        accountsInfo.accounts
      );
    const investAccounts: AccontTypeData<MarginAccount>['accounts'] =
      getMarginAccountsFromMarginResponse(
        accountsRests?.invest || null,
        accountsInfo.accounts
      );
    const leverageAccounts: AccontTypeData<MarginAccount>['accounts'] =
      getMarginAccountsFromMarginResponse(
        accountsRests?.leverage || null,
        accountsInfo.accounts
      );
    const earnAccounts: AccontTypeData<MarginAccount>['accounts'] =
      getMarginAccountsFromMarginResponse(
        accountsRests?.earn || null,
        accountsInfo.accounts
      );

    const ewalletRests = getRegularAccountsRests(ewalletAccounts);
    const spotRests = getRegularAccountsRests(spotAccounts);
    const investRests = getMarginAccountsRests(investAccounts);
    const leverageRests = getMarginAccountsRests(leverageAccounts);
    const earnRests = getMarginAccountsRests(earnAccounts);

    const {
      balance: ewalletBalance,
      deltaPercent: ewalletDeltaPercent,
      midnightBalance: ewalletMidnightBalance,
    } = getAccountBalanceWithDeltas('ewallet', ewalletRests);

    const {
      balance: investBalance,
      midnightBalance: investMidnightBalance,
      deltaPercent: investDeltaPercent,
    } = getAccountBalanceWithDeltas('invest', investRests);

    const {
      balance: spotBalance,
      deltaPercent: spotDeltaPercent,
      midnightBalance: spotMidnightBalance,
    } = getAccountBalanceWithDeltas('spot', spotRests);

    const {
      balance: leverageBalance,
      deltaPercent: leverageDeltaPercent,
      midnightBalance: leverageMidnightBalance,
    } = getAccountBalanceWithDeltas('leverage', leverageRests);

    const {
      balance: earnBalance,
      deltaPercent: earnDeltaPercent,
      midnightBalance: earnMidnightBalance,
    } = getAccountBalanceWithDeltas('earn', earnRests);

    const generalBalance = getGeneralBalance(
      ewalletBalance,
      spotBalance,
      leverageBalance,
      investBalance,
      earnBalance
    );

    const generalMidnightBalance = getGeneralMidnightBalance(
      ewalletMidnightBalance,
      spotMidnightBalance,
      investMidnightBalance,
      leverageMidnightBalance,
      earnMidnightBalance
    );

    const deltaPercent = getDeltaPercent(
      generalBalance,
      generalMidnightBalance
    );

    const baseAssetMeta = getBaseAssetMeta(accountsRests);

    const breakdown = getBalanceBreakdown({
      generalBalance,
      ewalletRests,
      spotRests,
      leverageRests,
      investRests,
      earnRests,
    });

    setGeneral({
      ...baseAssetMeta,
      ...breakdown,
      balance: generalBalance,
      deltaPercent,
    });

    setEwallet({
      ...baseAssetMeta,
      balance: ewalletBalance,
      deltaPercent: ewalletDeltaPercent,
      accounts: ewalletAccounts,
      created: getAccountTypeCreatedDate(accountsByType?.ewallet || []),
    });

    setInvest({
      ...baseAssetMeta,
      balance: investBalance,
      deltaPercent: investDeltaPercent,
      accounts: investAccounts,
      created: getAccountTypeCreatedDate(accountsByType?.invest || []),
    });

    setSpot({
      ...baseAssetMeta,
      balance: spotBalance,
      deltaPercent: spotDeltaPercent,
      accounts: spotAccounts,
      tradingAccKey: spotAccounts[0]?.tradingAccKey,
      created: getAccountTypeCreatedDate(accountsByType?.spot || []),
    });

    setLeverage({
      ...baseAssetMeta,
      balance: leverageBalance,
      deltaPercent: leverageDeltaPercent,
      accounts: leverageAccounts,
      created: getAccountTypeCreatedDate(accountsByType?.leverage || []),
    });

    setEarn({
      ...baseAssetMeta,
      balance: earnBalance,
      deltaPercent: earnDeltaPercent,
      accounts: earnAccounts,
      created: getAccountTypeCreatedDate(accountsByType?.earn || []),
    });
  }, [accountsRests, accountsByType, accountsInfo]);

  useEffect(() => {
    setAccountsQty(accountsInfo?.accounts?.length || 0);
  }, [accountsInfo]);
};
